/** @module ProductOptions */

import {
  getProductPackGenericEyeSerumFormulaId,
  getProductPackName,
  isProductPackGenericEyeSerum
} from '@/models/core/ProductPack';

/**
 * A set of product packs which have the same name but differ
 * in size.
 *
 * @typedef {Object} ProductOptions
 * @property {string} productName
 * @property {ProductPack[]} options
 */

/**
 * ProductOptions constructor.
 *
 * @function
 * @param {string} productName
 * @param {ProductPack[]} options - a list of different options of the same product (e.g. different sizes)
 * @return {ProductOptions}
 */
export const ProductOptions = (productName, options) => ({ productName, options });

/**
 * Get the name of a product in set.
 *
 * @function
 * @param {ProductOptions} productOptions
 * @return {string}
 */
export const getProductOptionsName = productOptions => productOptions.productName;

/**
 * Get options from set.
 *
 * @function
 * @param {ProductOptions} productOptions
 * @return {ProductPack[]}
 */
export const getProductOptionsProductPacksList = productOptions => productOptions.options;

/**
 * Check is an options set is empty.
 *
 * @function
 * @param {ProductOptions} productOptions
 * @return {boolean}
 */
export const isProductOptionsEmpty = productOptions =>
  !productOptions || !productOptions.options || productOptions.options.length === 0;

/**
 * Get a product pack from options set which is considered as default.
 *
 * @function
 * @param {ProductOptions} packOptionsSet
 * @return {ProductPack | null}
 */
export const getProductOptionsDefaultOption = packOptionsSet => {
  if (isProductOptionsEmpty(packOptionsSet)) {
    return null;
  }

  const { options } = packOptionsSet;
  const [firstOption] = options;

  if (isProductPackGenericEyeSerum(firstOption)) {
    return options.find(option => {
      const formulaId = getProductPackGenericEyeSerumFormulaId(option);

      return formulaId.startsWith('WRINKLES_');
    });
  }

  // TODO: do not rely on options order for finding default option
  return firstOption;
};

/**
 * Get product pack from options by formula id or default pack if provided id is empty.
 *
 * @function
 * @param {string} formulaId
 * @param {ProductOptions} productOptions
 * @return {ProductPack}
 */
export const getProductOptionsOptionByFormulaIdOrDefault = (formulaId, productOptions) => {
  if (!formulaId) {
    return getProductOptionsDefaultOption(productOptions);
  }

  return productOptions.options.find(
    option => formulaId === getProductPackGenericEyeSerumFormulaId(option)
  );
};

/**
 * Add product pack to a set of options.
 *
 * @function
 * @param {ProductPack} option
 * @param {ProductOptions} [productOptions]
 * @return {ProductOptions}
 */
export const addProductOptionsOption = (option, productOptions) => {
  const name = getProductPackName(option);

  if (!productOptions) {
    return ProductOptions(name, [option]);
  }

  return ProductOptions(name, [...productOptions.options, option]);
};
